// =============================================================================
// ESPOLIDAR WEB ADMIN - DASHBOARD PAGE
// =============================================================================

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useDevices } from '../hooks/useDevices'
import { useMedia } from '../hooks/useMedia'
import { wsService } from '../services/websocket'
import { Cpu, Image, Activity, Wifi, WifiOff, Clock } from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import type { WSMessage } from '../types'
import { formatDistanceToNow } from 'date-fns'
import { it } from 'date-fns/locale'

interface StatCardProps {
  title: string
  value: string | number
  icon: React.ReactNode
  color: string
  link?: string
}

function StatCard({ title, value, icon, color, link }: StatCardProps) {
  const content = (
    <div className={`bg-white rounded-xl p-6 shadow-sm card-hover border-l-4 ${color}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500 font-medium">{title}</p>
          <p className="text-3xl font-bold mt-1">{value}</p>
        </div>
        <div className="text-gray-400">{icon}</div>
      </div>
    </div>
  )

  if (link) {
    return <Link to={link}>{content}</Link>
  }
  return content
}

export default function DashboardPage() {
  const { data: devices, isLoading: devicesLoading } = useDevices()
  const { data: media, isLoading: mediaLoading } = useMedia()
  const [recentEvents, setRecentEvents] = useState<WSMessage[]>([])

  useEffect(() => {
    const unsubscribe = wsService.subscribe((message) => {
      if (message.type === 'device_event' || message.type === 'device_connected' || message.type === 'device_disconnected') {
        setRecentEvents((prev) => [message, ...prev].slice(0, 10))
      }
    })

    return unsubscribe
  }, [])

  if (devicesLoading || mediaLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  const onlineDevices = devices?.filter((d) => d.status === 'online').length || 0
  const totalDevices = devices?.length || 0
  const totalMedia = media?.length || 0

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Dashboard</h1>

      {/* Stats grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Dispositivi Online"
          value={`${onlineDevices}/${totalDevices}`}
          icon={<Cpu size={32} />}
          color="border-green-500"
          link="/devices"
        />
        <StatCard
          title="Dispositivi Totali"
          value={totalDevices}
          icon={<Activity size={32} />}
          color="border-blue-500"
          link="/devices"
        />
        <StatCard
          title="File Media"
          value={totalMedia}
          icon={<Image size={32} />}
          color="border-purple-500"
          link="/media"
        />
        <StatCard
          title="Eventi Oggi"
          value={recentEvents.length}
          icon={<Clock size={32} />}
          color="border-orange-500"
        />
      </div>

      {/* Devices and Events */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Devices list */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold mb-4">Dispositivi</h2>
          {devices && devices.length > 0 ? (
            <div className="space-y-3">
              {devices.slice(0, 5).map((device) => (
                <Link
                  key={device.id}
                  to={`/devices/${device.device_id}`}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-3 h-3 rounded-full ${
                        device.status === 'online'
                          ? 'bg-green-500'
                          : device.status === 'offline'
                          ? 'bg-red-500'
                          : 'bg-yellow-500'
                      }`}
                    />
                    <div>
                      <p className="font-medium">{device.name}</p>
                      <p className="text-sm text-gray-500">{device.device_id}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    {device.status === 'online' ? (
                      <span className="flex items-center gap-1 text-green-600 text-sm">
                        <Wifi size={14} /> Online
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-gray-400 text-sm">
                        <WifiOff size={14} /> Offline
                      </span>
                    )}
                    {device.last_seen && (
                      <p className="text-xs text-gray-400">
                        {formatDistanceToNow(new Date(device.last_seen), {
                          addSuffix: true,
                          locale: it,
                        })}
                      </p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">
              Nessun dispositivo registrato
            </p>
          )}
          {devices && devices.length > 5 && (
            <Link
              to="/devices"
              className="block text-center text-primary-600 hover:text-primary-700 mt-4 text-sm font-medium"
            >
              Vedi tutti i dispositivi
            </Link>
          )}
        </div>

        {/* Recent events */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold mb-4">Eventi Recenti</h2>
          {recentEvents.length > 0 ? (
            <div className="space-y-3">
              {recentEvents.map((event, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg"
                >
                  <div
                    className={`w-2 h-2 rounded-full mt-2 ${
                      event.type === 'device_connected'
                        ? 'bg-green-500'
                        : event.type === 'device_disconnected'
                        ? 'bg-red-500'
                        : 'bg-blue-500'
                    }`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {event.type === 'device_connected'
                        ? 'Dispositivo connesso'
                        : event.type === 'device_disconnected'
                        ? 'Dispositivo disconnesso'
                        : event.type}
                    </p>
                    <p className="text-xs text-gray-500">{event.device_id}</p>
                  </div>
                  <span className="text-xs text-gray-400 whitespace-nowrap">
                    {event.timestamp
                      ? formatDistanceToNow(new Date(event.timestamp), {
                          addSuffix: true,
                          locale: it,
                        })
                      : 'ora'}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">
              Nessun evento recente
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
